import { useEffect, useRef } from "react";

const SPACING = 176;
const REACH = 340;
const PUSH = 78;
const SPRING = 28;
const SPRING_DAMP = 2 * Math.sqrt(SPRING);

function circumcircle(a, b, c) {
    const A = b.x - a.x;
    const B = b.y - a.y;
    const C = c.x - a.x;
    const D = c.y - a.y;
    const E = A * (a.x + b.x) + B * (a.y + b.y);
    const F = C * (a.x + c.x) + D * (a.y + c.y);
    const G = 2 * (A * (c.y - b.y) - B * (c.x - b.x));
    if (Math.abs(G) < 1e-6) return null;
    const x = (D * E - B * F) / G;
    const y = (A * F - C * E) / G;
    return { x, y, r2: (x - a.x) ** 2 + (y - a.y) ** 2 };
}

function delaunay(points) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    points.forEach((p) => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    const d = Math.max(maxX - minX, maxY - minY) || 1;
    const mx = (minX + maxX) / 2;
    const my = (minY + maxY) / 2;
    const s1 = { x: mx - 20 * d, y: my - d };
    const s2 = { x: mx, y: my + 20 * d };
    const s3 = { x: mx + 20 * d, y: my - d };
    let triangles = [[s1, s2, s3]];

    points.forEach((p) => {
        const bad = [];
        triangles.forEach((t) => {
            const cc = circumcircle(t[0], t[1], t[2]);
            if (!cc) return;
            const dx = p.x - cc.x;
            const dy = p.y - cc.y;
            if (dx * dx + dy * dy <= cc.r2 + 0.01) bad.push(t);
        });

        const edges = [];
        bad.forEach((t) => {
            [
                [t[0], t[1]],
                [t[1], t[2]],
                [t[2], t[0]],
            ].forEach((edge) => {
                const dup = edges.findIndex(
                    (other) =>
                        (other[0] === edge[0] && other[1] === edge[1]) ||
                        (other[0] === edge[1] && other[1] === edge[0]),
                );
                if (dup >= 0) edges.splice(dup, 1);
                else edges.push(edge);
            });
        });

        triangles = triangles.filter((t) => !bad.includes(t));
        edges.forEach(([a, b]) => triangles.push([a, b, p]));
    });

    return triangles.filter((t) => t.every((p) => p !== s1 && p !== s2 && p !== s3));
}

function poisson(width, height, radius) {
    const bleed = radius * 0.55;
    const x0 = -bleed;
    const y0 = -bleed;
    const x1 = width + bleed;
    const y1 = height + bleed;
    const cell = radius / Math.SQRT2;
    const cols = Math.max(1, Math.ceil((x1 - x0) / cell));
    const rows = Math.max(1, Math.ceil((y1 - y0) / cell));
    const grid = new Array(cols * rows).fill(-1);
    const points = [];
    const active = [];
    const min2 = radius * radius;

    const cellOf = (x, y) => ({
        gx: Math.floor((x - x0) / cell),
        gy: Math.floor((y - y0) / cell),
    });

    const crowded = (x, y) => {
        const { gx, gy } = cellOf(x, y);
        for (let j = gy - 2; j <= gy + 2; j += 1) {
            if (j < 0 || j >= rows) continue;
            for (let i = gx - 2; i <= gx + 2; i += 1) {
                if (i < 0 || i >= cols) continue;
                const index = grid[j * cols + i];
                if (index < 0) continue;
                const p = points[index];
                const dx = p.x - x;
                const dy = p.y - y;
                if (dx * dx + dy * dy < min2) return true;
            }
        }
        return false;
    };

    const add = (x, y) => {
        const { gx, gy } = cellOf(x, y);
        points.push({ x, y });
        active.push(points.length - 1);
        if (gx >= 0 && gy >= 0 && gx < cols && gy < rows) grid[gy * cols + gx] = points.length - 1;
    };

    add(width * 0.5, height * 0.45);

    let guard = 0;
    while (active.length && guard < 9000) {
        guard += 1;
        const slot = Math.floor(Math.random() * active.length);
        const origin = points[active[slot]];
        let placed = false;
        for (let k = 0; k < 30; k += 1) {
            const angle = Math.random() * Math.PI * 2;
            const dist = radius * (1 + Math.random());
            const x = origin.x + Math.cos(angle) * dist;
            const y = origin.y + Math.sin(angle) * dist;
            if (x < x0 || y < y0 || x > x1 || y > y1) continue;
            if (crowded(x, y)) continue;
            add(x, y);
            placed = true;
            break;
        }
        if (!placed) active.splice(slot, 1);
    }

    return points;
}

function makeNode(point, width, height) {
    const edge = point.x < 18 || point.y < 18 || point.x > width - 18 || point.y > height - 18;
    return {
        x: point.x,
        y: point.y,
        ax: (edge ? 12 : 54) + Math.random() * (edge ? 10 : 40),
        ay: (edge ? 10 : 48) + Math.random() * (edge ? 10 : 38),
        px: Math.random() * Math.PI * 2,
        py: Math.random() * Math.PI * 2,
        qx: Math.random() * Math.PI * 2,
        qy: Math.random() * Math.PI * 2,
        sx: (edge ? 0.16 : 0.46) + Math.random() * (edge ? 0.14 : 0.5),
        sy: (edge ? 0.14 : 0.4) + Math.random() * (edge ? 0.12 : 0.46),
        ox: 0,
        oy: 0,
        vx: 0,
        vy: 0,
    };
}

function smoothstep(edge) {
    const u = Math.min(1, Math.max(0, edge));
    return u * u * (3 - 2 * u);
}

function drift(t, amp, speed, phase, wobble) {
    return Math.sin(t * speed + phase) * amp + Math.sin(t * speed * 0.46 + wobble) * amp * 0.42;
}

export default function HeroWire() {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext("2d");
        if (!ctx) return undefined;

        let nodes = [];
        let edges = [];
        let builtW = 0;
        let builtH = 0;
        let raf = 0;
        let lastT = 0;
        const pointer = { x: 0, y: 0, sx: 0, sy: 0, inside: false };
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const rebuild = (w, h) => {
            if (edges.length && Math.abs(w - builtW) < 32 && Math.abs(h - builtH) < 32) return;
            builtW = w;
            builtH = h;
            const placed = poisson(w, h, SPACING);
            nodes = placed.map((point) => makeNode(point, w, h));
            const rest = nodes.map((node) => ({ x: node.x, y: node.y }));
            const seen = new Set();
            const maxLen = SPACING * 2.35;
            edges = [];
            delaunay(rest).forEach((tri) => {
                for (let i = 0; i < 3; i += 1) {
                    const a = tri[i];
                    const b = tri[(i + 1) % 3];
                    const ai = rest.indexOf(a);
                    const bi = rest.indexOf(b);
                    if (ai < 0 || bi < 0) continue;
                    const key = ai < bi ? `${ai}-${bi}` : `${bi}-${ai}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    if (dx * dx + dy * dy > maxLen * maxLen) continue;
                    edges.push([ai, bi]);
                }
            });
        };

        const resize = () => {
            const parent = canvas.parentElement;
            if (!parent) return;
            const { width, height } = parent.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.max(1, Math.floor(width * dpr));
            canvas.height = Math.max(1, Math.floor(height * dpr));
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const draw = (t) => {
            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            if (w < 2 || h < 2) return;
            rebuild(w, h);
            ctx.clearRect(0, 0, w, h);

            const dt = lastT ? Math.min(0.05, Math.max(0, t - lastT)) : 0.016;
            lastT = t;
            if (pointer.inside) {
                const follow = 1 - Math.exp(-dt * 6);
                pointer.sx += (pointer.x - pointer.sx) * follow;
                pointer.sy += (pointer.y - pointer.sy) * follow;
            }

            const live = nodes.map((node) => {
                const baseX = node.x + drift(t, node.ax, node.sx, node.px, node.qx);
                const baseY = node.y + drift(t, node.ay, node.sy, node.py, node.qy);
                let tx = 0;
                let ty = 0;
                if (pointer.inside) {
                    const dx = baseX - pointer.sx;
                    const dy = baseY - pointer.sy;
                    const dist = Math.hypot(dx, dy);
                    if (dist < REACH && dist > 0.001) {
                        const push = PUSH * smoothstep(1 - dist / REACH);
                        tx = (dx / dist) * push;
                        ty = (dy / dist) * push;
                    }
                }
                node.vx += ((tx - node.ox) * SPRING - node.vx * SPRING_DAMP) * dt;
                node.vy += ((ty - node.oy) * SPRING - node.vy * SPRING_DAMP) * dt;
                node.ox += node.vx * dt;
                node.oy += node.vy * dt;
                return { x: baseX + node.ox, y: baseY + node.oy };
            });

            ctx.lineWidth = 1;
            ctx.strokeStyle = "rgba(43, 80, 255, 0.08)";
            ctx.beginPath();
            edges.forEach(([ai, bi]) => {
                const a = live[ai];
                const b = live[bi];
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
            });
            ctx.stroke();

            ctx.fillStyle = "rgba(43, 80, 255, 0.16)";
            live.forEach((point) => {
                if (point.x < -24 || point.y < -24 || point.x > w + 24 || point.y > h + 24) return;
                ctx.beginPath();
                ctx.arc(point.x, point.y, 2.2, 0, Math.PI * 2);
                ctx.fill();
            });
        };

        resize();
        const parent = canvas.parentElement;
        const ro = new ResizeObserver(resize);
        if (parent) ro.observe(parent);

        const onMove = (event) => {
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const inside = x >= 0 && y >= 0 && x <= rect.width && y <= rect.height;
            if (inside && !pointer.inside) {
                pointer.sx = x;
                pointer.sy = y;
            }
            pointer.x = x;
            pointer.y = y;
            pointer.inside = inside;
        };
        const onLeave = () => {
            pointer.inside = false;
        };
        parent?.addEventListener("mousemove", onMove);
        parent?.addEventListener("mouseleave", onLeave);

        const loop = (now) => {
            draw(now / 1000);
            raf = requestAnimationFrame(loop);
        };

        if (reduce) draw(0.8);
        else raf = requestAnimationFrame(loop);

        const onHide = () => {
            if (document.hidden) {
                cancelAnimationFrame(raf);
                raf = 0;
            } else if (!raf && !reduce) raf = requestAnimationFrame(loop);
        };
        document.addEventListener("visibilitychange", onHide);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            parent?.removeEventListener("mousemove", onMove);
            parent?.removeEventListener("mouseleave", onLeave);
            document.removeEventListener("visibilitychange", onHide);
        };
    }, []);

    return (
        <canvas
            ref={ref}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full [mask-image:linear-gradient(180deg,#000_0%,#000_92%,transparent_100%)]"
        />
    );
}
